import React, { FC } from "react";
import ComplexPicker from "..";

import { IItemData } from "../common.typing";

import locale from "../../../_util/locale";

export interface IUserPickerProps {
    /** 组件的标题 */
    title?: string;
    /** 是否为多选 */
    multiple?: boolean;
    /** 数量超出时的提示 */
    limitTips?: string;
    /** 可选的最大数量 */
    maxNum?: number;
    /**
     * 不可用 id
     * 包括 group、user、org
     */
    disabledids?: string[];
    /**
     * 已选择的
     * 包括 group、user、org
     */
    pickedList?: IItemData[];
    /**
     * 必须选择的
     * 包括 group、user、org
     */
    requiredList?: IItemData[];
    /** 常用联系人（扁平列表） */
    commonlyList?: IItemData[];
    /** 常用联系人（A-Z 分组数据） */
    commonlyGrouped?: { [letter: string]: IItemData[] };
    /** 最近联系人 */
    recentConcactsList?: IItemData[];
    /** 获取当前用户的组织架构信息，用于 home 页 */
    getPersonOrg: () => void;
    /** 根据部门 id 获取组织架构信息 */
    getOrgList: (obj?: IItemData) => void;
    /** 组织架构展开信息 */
    orgList: IItemData[];
    /** 组织架构下的成员 */
    orgUser: IItemData[];
    /** 组织架构面包屑 */
    orgBread?: IItemData[];
    /** 个人组织架构信息 */
    personOrg?: IItemData;
    /** 是否展示【我的群组】入口 */
    showGroups?: boolean;
    /** 是否展示【外部联系人】入口，默认为 false */
    showExternalContact?: boolean;
    /** 群成员列表的群 id */
    groupId?: number | string;
    /** 群成员列表的群名称 */
    groupName?: string;
    /** 获取我的群组列表 */
    getMyGroupList?: () => void;
    /** 我的群组列表 */
    myGroupList?: IItemData[];
    /** 根据群 id 获取群成员 */
    getGroupMemberList?: (id: number | string) => void;
    /** 群成员列表（扁平列表） */
    groupMemberList?: IItemData[];
    /** 群成员列表（A-Z 分组数据） */
    groupMemberGrouped?: { [letter: string]: IItemData[] };
    /**
     * 左侧搜索事件
     * key： 关键词
     * rows：加载条数
     */
    loadSearchData: (key: string, rows?: number) => Promise<any>;
    /**
     * 加载更多搜索结果
     * type：类型 - user、group
     * key：关键词
     * row：加载条数，默认 20，赞不支持自定义
     */
    loadSearchDataMore?: (
        type: string,
        key: string,
        rows: number
    ) => Promise<any>;
    /** 关闭组件方法 */
    onClose: () => void;
    /** 点击确定 */
    onConfirm: (ids: string[], length: number) => void;
    /** 是否展示确认按钮的数量 */
    showSelectedNum?: boolean;
    /** 确认按钮是否为 loading 态 */
    isConfirmLoading?: boolean;
    /** 左侧搜索的 placeholder */
    searchPlaceholder?: string;
    showPartner?: boolean;
    /** 获取我的伙伴列表 */
    getPartnerList?: () => Promise<any>;
    /** 根据群 id 获取我的伙伴成员 */
    getPartnerMemberList?: (
        id: number | string,
        show_external: number
    ) => Promise<any>;
    /** 获取我的伙伴成员分组数据 */
    getPartnerMemberGrouped?: (
        id: number | string,
        show_external: number
    ) => Promise<any>;
    // 获取外部联系人列表
    getContactList?: (last_id: number, pageSize: number) => Promise<any>;

    /** 是否展示【最近会话】入口，default：false */
    showRecentSession?: boolean;
    /** 获取最近会话列表 */
    getRecentSessionList?: () => void;
    /** 最近会话列表 */
    recentSessionList?: IItemData[];

    /** 是否展示批量导入 */
    showBatchImport?: boolean;
    /** 批量导入接口 */
    batchImportApi?: (workCodes: string[]) => Promise<any>;

    /** 可选总数为 0 时确认按钮可点击，默认为 false */
    canSelectZero?: boolean;

    /** 隐藏群组成员页面标题 */
    isHideGroupMemberPageTitle?: boolean;

    /** 是否展示群组成员角色 */
    isShowGroupMemberRole?: boolean;
    /** 是否展示群组成员全选 */
    showGroupMemberCheckAll?: boolean;
    /** 搜索预留内容区域 */
    mainChildren?: React.ReactNode;
    /** 是否只展示选中的数量,不显示最大数量 */
    justShowSelectedNoMax?: boolean;
    /** 是否从组织架构开始选人，如超管 */
    orginSelect?: boolean;
    /** 是否禁用标题拖拽 */
    isDisableTitleDrag?: boolean;
    /** 埋点方法 */
    track?: (eventName: string, eventData?: any) => void;
}

/**
 * 选人组件：
 * 支持选择我的群组内成员、组织架构下成员、常用联系人，
 * 支持【我的群组】入口配置。
 */
export const UserPicker: FC<IUserPickerProps> = ({
    // page,
    onClose,
    onConfirm,
    title = locale("userPickerTitleDefault"),
    multiple = true,
    limitTips = locale("common_select_overrun"),
    maxNum = 1,
    showGroups = false,
    showExternalContact = false,

    groupId,
    groupName,
    disabledids,
    pickedList,
    requiredList,
    commonlyList,
    commonlyGrouped,
    recentConcactsList,
    getPersonOrg,
    getOrgList,
    orgList,
    orgUser,
    orgBread,
    personOrg,
    getMyGroupList,
    myGroupList,
    getGroupMemberList,
    groupMemberList,
    groupMemberGrouped,
    loadSearchData,
    loadSearchDataMore,
    showSelectedNum = true,
    isConfirmLoading = false,

    showRecentSession = false,
    getRecentSessionList,
    recentSessionList,

    searchPlaceholder = locale("searchPlaceholderDefault"),
    showPartner,
    getPartnerList,
    getPartnerMemberList,
    getPartnerMemberGrouped,
    getContactList,

    showBatchImport,
    batchImportApi,

    canSelectZero = false,

    isHideGroupMemberPageTitle = false,
    isShowGroupMemberRole,
    showGroupMemberCheckAll,
    mainChildren,
    justShowSelectedNoMax = false,
    orginSelect = false,
    isDisableTitleDrag,
    track,
}: IUserPickerProps) => {
    const userPickerProps = {
        type: "member",
        searchType:
            groupId && isHideGroupMemberPageTitle //此情况为限定到群里，不允许返回到首页进行其他功能，所以也不能搜群
                ? ["user"]
                : ["user", "group"],
        page: "home",
        isShowRecentConcacts: true,
        onClose,
        onConfirm,
        title,
        multiple,
        limitTips,
        maxNum,
        disabledids,
        pickedList,
        requiredList,
        commonlyList,
        commonlyGrouped,
        recentConcactsList,
        getPersonOrg,
        getOrgList,
        orgList,
        orgUser,
        orgBread,
        personOrg,
        showGroups,
        showExternalContact,
        groupId,
        groupName,
        getMyGroupList,
        myGroupList,
        getGroupMemberList,
        groupMemberList,
        groupMemberGrouped,
        loadSearchData,
        loadSearchDataMore,
        showSelectedNum,
        isConfirmLoading,
        searchPlaceholder,
        showPartner,
        getPartnerList,
        getPartnerMemberList,
        getPartnerMemberGrouped,
        getContactList,

        showRecentSession,
        getRecentSessionList,
        recentSessionList,

        showBatchImport,
        batchImportApi,

        canSelectZero,
        isHideGroupMemberPageTitle,
        isShowGroupMemberRole,
        showGroupMemberCheckAll,
        mainChildren,
        justShowSelectedNoMax,
        orginSelect,
        isDisableTitleDrag,
        track,
    };
    return <ComplexPicker {...userPickerProps} />;
};

export default UserPicker;
